import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2"; // Import correct de Grid2
import { useDropzone } from "react-dropzone";
import CloseIcon from '@mui/icons-material/Close';
import ValidationStrategyFactory from './strategies/ValidationStrategyFactory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TARGET = process.env.REACT_APP_TARGET || 'google';

function App() {
  const [token, setToken] = useState("");
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null); // Pour afficher une image en grand
  const [imageUrls, setImageUrls] = useState([]); // Pour stocker les URLs des objets
  const [validationStrategy] = useState(() => ValidationStrategyFactory.createStrategy(TARGET));

  const verifyToken = async (token) => {
    try {
      await axios.get(`${BACKEND_URL}/validate`, {
        headers: {
          Authorization: token,
          'X-Target': TARGET,
        },
      });
      return true;
    }
    catch (error) {
      setError("Identifiant non reconnu. Merci d'accéder à cette page depuis g-nair.");
      return false;
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const urlToken = queryParams.get("token");
    if (urlToken) {
      verifyToken(urlToken).then((isValid) => {
        if (isValid) {
          setToken(urlToken);
        }
      });
      
    } else {
      setError("Identifiant non reconnu. Merci d'accéder à cette page depuis g-nair.");
    }
  }, []);

  // Nettoyer les URLs d'objets quand le composant se démonte
  useEffect(() => {
    return () => {
      imageUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  const onDrop = async (acceptedFiles) => {
    setError("");
    setQuotaExceeded(false);

    const validationResults = await Promise.all(
      acceptedFiles.map(async (file) => {
        const result = await validationStrategy.validate(file);
        return { file, ...result };
      })
    );

    const validFiles = validationResults
      .filter(result => result.isValid)
      .map(result => result.file);

    const invalidFiles = validationResults.filter(result => !result.isValid);

    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles
        .map(result => result.errorMessage)
        .filter((message, index, array) => array.indexOf(message) === index); // Dédoublonner
      
      setError(
        `Certains fichiers ont été ignorés : ${errorMessages.join(", ")}`
      );
    }

    // Créer les URLs d'objets pour les fichiers valides
    const newUrls = validFiles.map(file => URL.createObjectURL(file));

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    setImageUrls((prevUrls) => [...prevUrls, ...newUrls]);
  };

  const handleRemoveFile = (index) => {
    // Nettoyer l'URL de l'objet avant de supprimer le fichier
    if (imageUrls[index]) {
      URL.revokeObjectURL(imageUrls[index]);
    }
    
    const updatedFiles = [...files];
    const updatedUrls = [...imageUrls];
    updatedFiles.splice(index, 1);
    updatedUrls.splice(index, 1);
    
    setFiles(updatedFiles);
    setImageUrls(updatedUrls);
  };

  const handleUpload = async () => {
    if (!token) {
      setError("Le token est requis.");
      return;
    }

    if (files.length === 0) {
      setError("Veuillez sélectionner au moins une image.");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const base64Files = await Promise.all(
        files.map((file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                content: reader.result.split(",")[1],
                name: file.name,
                type: file.type,
              });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
        )
      );

      await axios.post(
        `${BACKEND_URL}/upload`,
        { images: base64Files },
        {
          headers: {
            Authorization: token,
            'X-Target': TARGET,
          },
        }
      );

      setFiles([]);
      setSuccessMessage("Toutes les images ont été chargées avec succès !");
    } catch (error) {
      if (
        error.response?.status === 400 &&
        error.response?.data?.error === "Quota exceeded. Max 20 images allowed."
      ) {
        setQuotaExceeded(true);
        setError(
          "Vous avez atteint le quota de 20 images. Veuillez supprimer des images depuis g-nair pour continuer."
        );
      } else {
        setError(
          error.response?.data?.error ||
            "Une erreur s'est produite lors du chargement de(s) image(s)."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: isLoading || quotaExceeded || !token,
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
  });

  return (
    <Container maxWidth="md">
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h4" gutterBottom>
          G-Nair - Envoi d'images
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}
        
        <Paper
          {...getRootProps()}
          sx={{
            border: "2px dashed #1976d2",
            padding: 3,
            mt: 2,
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <Typography>Glissez vos images ici, ou cliquez pour les sélectionner.</Typography>
          <Typography variant="caption">
            ({validationStrategy.getHelpMessage()})
          </Typography>
        </Paper>

        {files.length > 0 && (
          <Box mt={2}>
            <Typography variant="h6">Prévisualisation :</Typography>
            <Grid container spacing={2}>
              {files.map((file, index) => (
                <Grid xs={6} sm={4} md={3} key={index}>
                  <Box position="relative">
                    <img
                      src={imageUrls[index]}
                      alt={file.name}
                      style={{
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedImage(imageUrls[index])}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        color: "white",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleUpload}
          disabled={isLoading || quotaExceeded || !token}
          sx={{ mt: 2 }}
        >
          {isLoading ? <CircularProgress size={24} /> : "Charger les images"}
        </Button>

        {quotaExceeded && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Vous avez atteint le quota de 20 images. Veuillez supprimer des
            images depuis g-nair pour continuer.
          </Alert>
        )}
      </Box>

      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
      >
        <DialogContent>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Prévisualisation"
              style={{ width: "100%", height: "auto" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}

export default App;
